import { Pill } from './Pill';
import { EmptyPanel, ErrorPanel } from './StatePanel';
import { useCollection } from '../hooks/useRadarQuery';
import { label } from '../lib/format';
import type { CollectionStatus, FeedCollectionStatus } from '../lib/types';

/**
 * How each verdict is tinted.
 *
 * A `Record` over the union rather than a lookup with a fallback, so adding a
 * status fails the typecheck instead of rendering it in the neutral tint — an
 * unrecognised state that looks calm is the failure this panel exists to catch.
 * `failing` and `stalled` share a tint because both need a person; the word is
 * shown beside it, and the tint is not carrying the distinction.
 */
const TONE: Record<CollectionStatus, 'positive' | 'accent' | 'warning'> = {
  ok: 'positive',
  overdue: 'accent',
  failing: 'warning',
  stalled: 'warning',
};

/**
 * What each verdict means for the reader, in the words of the action it implies.
 *
 * Stated here rather than left to the status word alone: "stalled" and
 * "overdue" are close enough in ordinary use to read as synonyms, and the
 * difference between them is the difference between a pass that is behind and
 * no pass at all.
 */
const MEANING: Record<CollectionStatus, string> = {
  ok: 'Every configured feed is being read on schedule.',
  overdue: 'Some feeds are behind their next scheduled read.',
  failing: 'A source is refusing reads. Check the key or the adapter.',
  stalled: 'Nothing is reading at least one feed at all. No collector is running for it.',
};

/** Renders one feed's counts as the phrases that name what to do about them. */
function FeedNote({ feed }: { feed: FeedCollectionStatus }) {
  const notes = [
    feed.neverRead ? `${feed.neverRead} never read` : null,
    feed.overdue ? `${feed.overdue} overdue` : null,
    feed.failing ? `${feed.failing} failing (worst run ${feed.worstConsecutiveFailures})` : null,
    feed.hoursSinceCheck == null ? 'never read' : `last read ${Math.round(feed.hoursSinceCheck)}h ago`,
  ].filter((note): note is string => note !== null);
  return <small>{notes.join(' · ')}</small>;
}

/**
 * Whether anything is still reading the sources this registry is built from.
 *
 * The poll ledger was written by four collectors and read by nothing — no
 * route, no page, no command — so a collector that stopped, or one that was
 * never started, produced exactly the same product as one working perfectly.
 * Measured on 2026-08-11: 24 of 24 tracked feeds were overdue, SEC EDGAR had
 * not been read in 47 hours, and 56 of 75 companies had neither a job board nor
 * a curated register key, so no collector could reach them at all.
 *
 * Coverage sits beside the verdict rather than inside it. A company no feed can
 * reach is a curation gap and a stalled feed is an operational one; folding the
 * first into the second would leave the headline permanently bad as the
 * registry grows, which is how a status stops being looked at.
 */
export function CollectionPanel() {
  const query = useCollection();
  if (query.isError) return <ErrorPanel error={query.error} />;
  if (!query.data) return <EmptyPanel message="Reading collection state…" />;
  const { status, coverage, feeds } = query.data;
  return (
    <div className="panel">
      <div className="panel__heading">
        <div><span className="eyebrow">Collection</span><h2>Are the sources still being read?</h2></div>
        <Pill value={status} tone={TONE[status]} />
      </div>
      <p className="collection-verdict">{MEANING[status]}</p>
      <p className="collection-coverage">
        <strong>{coverage.reachable}</strong> of {coverage.companies} companies are reachable by some feed.
        {coverage.unreachable.length > 0 && ` ${coverage.unreachable.length} have neither a job board nor a register key, so nothing collects for them: ${coverage.unreachable.join(', ')}.`}
      </p>
      {feeds.length
        ? (
          <div className="momentum-list">
            {feeds.map((feed) => (
              <div className="collection-feed" key={feed.feed}>
                <div>
                  <strong>{label(feed.feed)}</strong>
                  <FeedNote feed={feed} />
                </div>
                <span>{feed.expected}</span>
                <Pill value={feed.status} tone={TONE[feed.status]} />
              </div>
            ))}
          </div>
        )
        : <EmptyPanel message="No company configures a job board or a register key yet." />}
    </div>
  );
}
